import type { NavMenuContentType } from '@hhgtech/hhg-components/components/navigationV2/index'
import type { GetServerSideProps } from 'next'

import { callApiNew } from 'api/axios'
import { formatExpertUser } from 'api/dataTransform'
import { PATHS } from 'api/paths'
import { checkMobile } from 'api/utils/validators'
import { cleanUnicodeUrl } from 'api/utils/paths'
import ExpertPageTemplate from 'components/pages/expert'
import type { ExpertUser, FooterInfo } from 'interfaces/types'
import { getFooterDataPersist } from 'utils/isomorphicCache/footerData'
import { getNavMenuContentPersist } from 'utils/isomorphicCache/navMenuContent'

export type Props = {
  expert: ExpertUser
  isMobileSsr: boolean
  footerInfo: FooterInfo
  dataNavMenuContent?: NavMenuContentType
}

const AuthorPage = ({
  expert,
  isMobileSsr,
  footerInfo,
  dataNavMenuContent,
}: Props) => {
  return (
    <ExpertPageTemplate
      type="author"
      expert={expert}
      isMobileSsr={isMobileSsr}
      footerInfo={footerInfo}
      dataNavMenuContent={dataNavMenuContent}
    />
  )
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { query, locale, req } = ctx
  const userAgent = `${req.headers['user-agent']}`
  const isMobile = checkMobile(userAgent)
  const { slug } = query

  const finalSlug = Array.isArray(slug) ? slug.join('/') : slug
  const [expertRes, footerInfo, dataNavMenuContent] = await Promise.all([
    callApiNew(PATHS.COMMON.GET_AUTHOR, {
      params: {
        slug: finalSlug || '',
        ...[locale === 'en-PH' ? { lang: 'en' } : {}],
      },
    }),
    getFooterDataPersist(),
    getNavMenuContentPersist(locale),
  ])

  if (expertRes.status === 2 && expertRes.data?.redirect_url) {
    return {
      redirect: {
        destination: cleanUnicodeUrl(expertRes.data.redirect_url),
        statusCode: 301,
      },
    }
  }

  if (
    !expertRes ||
    expertRes.status !== 1 ||
    expertRes?.data?.turn_off_profile ||
    !expertRes?.data?.role
  ) {
    return {
      notFound: true,
    }
  }

  if (['expert', 'medical'].includes(expertRes?.data?.role || '')) {
    return {
      redirect: {
        destination: cleanUnicodeUrl(`/expert/${finalSlug || ''}`),
        statusCode: 301,
      },
    }
  }

  return {
    props: {
      isMobileSsr: isMobile,
      expert: formatExpertUser(expertRes.data),
      footerInfo: footerInfo?.data || null,
      dataNavMenuContent: dataNavMenuContent || {},
    },
  }
}

export default AuthorPage
